import type { VarlessCopyId } from '@npc/copy'

/**
 * 12 态真实状态合同。
 *
 * 设计稿 §01 把这 12 档的**文案与「能否动画」**一起钉死了。两件事必须一起读：
 * 文案说的是「现在是什么」，动画说的是「我们敢不敢让它看起来在动」。
 *
 * # 为什么动画是合同的一部分，而不是视觉细节
 *
 * 这个产品最容易做坏的地方不是界面，是**让人分不清哪些是真发生的、
 * 哪些是界面在演**。一个转圈的图标是一句断言：「此刻有东西在推进」。
 * 而断线之后我们并不知道这句话还成不成立——继续转就是在骗人。
 *
 * 所以 `disconnected` 与 `unknown` 两档在设计稿里标的是「禁止」而不是「不可」：
 * 前者是「这一档下不该有动画」，后者是「这一档下有动画就是撒谎」。
 * 落到代码里两者都是 `mayAnimate: false`，**刻意不为这个区别造一个三值枚举**——
 * 一个永远塌缩成同一个布尔的枚举，是留给下一个人的一道没有答案的题。
 * 区别记在这里，因为它解释的是「为什么这两档不许有例外」。
 */
export const SESSION_STATES = [
  'planning',
  'discussing',
  'running',
  'waiting_user',
  'waiting_agent',
  'blocked',
  'disconnected',
  'recovering',
  'platform_held',
  'failed',
  'completed',
  'unknown',
] as const

export type SessionState = (typeof SESSION_STATES)[number]

/** 设计稿 TONE 的五档。`a` 是「强调」不是「成功」——进行中不该长得像已完成。 */
export type Tone = 'n' | 'g' | 'w' | 'r' | 'a'

export interface SessionStateFacts {
  readonly copyId: VarlessCopyId
  readonly tone: Tone
  /** 这一档下允不允许出现转圈、呼吸点、进度条这类「正在推进」的视觉。 */
  readonly mayAnimate: boolean
}

/**
 * 12 态查表。`Record<SessionState, …>` 不是装饰：**少写一档编译期就红**，
 * 于是「加了一个状态但忘了决定它能不能动画」这件事没法悄悄发生。
 */
export const SESSION_STATE_FACTS: Record<SessionState, SessionStateFacts> = {
  planning: { copyId: 'lulu_workbench.state.planning', tone: 'n', mayAnimate: true },
  discussing: { copyId: 'lulu_workbench.state.discussing', tone: 'n', mayAnimate: true },
  running: { copyId: 'lulu_workbench.state.running', tone: 'a', mayAnimate: true },
  waiting_user: { copyId: 'lulu_workbench.state.waiting_user', tone: 'w', mayAnimate: false },
  waiting_agent: { copyId: 'lulu_workbench.state.waiting_agent', tone: 'w', mayAnimate: false },
  blocked: { copyId: 'lulu_workbench.state.blocked', tone: 'w', mayAnimate: false },
  // 禁止档之一：断线期间任何动画都是在断言一件我们并不知道的事。
  disconnected: { copyId: 'lulu_workbench.state.disconnected', tone: 'w', mayAnimate: false },
  recovering: { copyId: 'lulu_workbench.state.recovering', tone: 'n', mayAnimate: true },
  platform_held: { copyId: 'lulu_workbench.state.platform_held', tone: 'w', mayAnimate: false },
  failed: { copyId: 'lulu_workbench.state.failed', tone: 'r', mayAnimate: false },
  completed: { copyId: 'lulu_workbench.state.completed', tone: 'g', mayAnimate: false },
  // 禁止档之二。没有事件就是这一档，而不是退回一个看起来正常的默认值。
  unknown: { copyId: 'lulu_workbench.state.unknown', tone: 'n', mayAnimate: false },
}

/** 事件流此刻通不通。断了不等于出错，但它改变界面敢说什么。 */
export type LinkState = 'live' | 'down'

/**
 * 会话级状态投影。
 *
 * 两条规则，都来自设计稿而不是工程便利：
 *
 * 1. **没有事件就是 `unknown`。** 不是 `planning`，也不是「默认先显示进行中」。
 *    一个还没收到任何事件的会话显示成正在跑，用户会去等一个不会来的结果。
 * 2. **断线时会话级一律 `disconnected`。** 这一档自己的文案就说了「现场已保留」——
 *    它同时回答了用户在这一刻的两个问题：现在不知道，以及东西没丢。
 */
export function projectSessionState(reported: SessionState | null, link: LinkState): SessionState {
  if (link === 'down') return 'disconnected'
  return reported ?? 'unknown'
}

/**
 * 工作项与子 Agent 的状态药丸。设计稿 `items()` 的 SD 表，11 档。
 *
 * 与会话级那 12 档是两套：会话说的是「这条会话整体在干嘛」，
 * 这一套说的是「这一件活现在什么样」。合成一套会让「会话在跑」和
 * 「这一项在跑」共用一个词，而断线时这两句话的真假并不一样。
 */
export const ITEM_STATES = [
  'todo',
  'run',
  'wait',
  'block',
  'done',
  'fail',
  'rec',
  'held',
  'none',
  'unknown',
  'closed',
] as const

export type ItemState = (typeof ITEM_STATES)[number]

export interface ItemStateFacts {
  readonly copyId: VarlessCopyId
  readonly tone: Tone
  readonly mayAnimate: boolean
}

export const ITEM_STATE_FACTS: Record<ItemState, ItemStateFacts> = {
  todo: { copyId: 'lulu_workbench.item.todo', tone: 'n', mayAnimate: false },
  run: { copyId: 'lulu_workbench.item.run', tone: 'a', mayAnimate: true },
  wait: { copyId: 'lulu_workbench.item.wait', tone: 'w', mayAnimate: false },
  block: { copyId: 'lulu_workbench.item.block', tone: 'w', mayAnimate: false },
  done: { copyId: 'lulu_workbench.item.done', tone: 'g', mayAnimate: false },
  fail: { copyId: 'lulu_workbench.item.fail', tone: 'r', mayAnimate: false },
  rec: { copyId: 'lulu_workbench.item.rec', tone: 'n', mayAnimate: true },
  held: { copyId: 'lulu_workbench.item.held', tone: 'w', mayAnimate: false },
  none: { copyId: 'lulu_workbench.item.none', tone: 'n', mayAnimate: false },
  unknown: { copyId: 'lulu_workbench.item.unknown', tone: 'w', mayAnimate: false },
  closed: { copyId: 'lulu_workbench.item.closed', tone: 'n', mayAnimate: false },
}

/**
 * 断线也不会变的那几档。
 *
 * 判据是「这一档描述的是已经发生过的事，还是此刻正在发生的事」：
 *
 *   已经发生（留着）  done / fail / closed —— 收据、失败、关闭都是持久事实
 *   还没发生（留着）  todo / none          —— 「还没开始」「没人认领」是方案的结构，不是心跳
 *   此刻的状态（撤掉） run / wait / block / rec / held
 *
 * 中间那三档最容易被漏掉：`wait` 看起来是静止的，但「它还在等」同样是一句
 * 关于此刻的断言——连接断了之后，它可能早就等到并跑完了。
 */
const SURVIVES_DISCONNECT: ReadonlySet<ItemState> = new Set<ItemState>([
  'todo',
  'none',
  'done',
  'fail',
  'closed',
])

/**
 * 工作项级状态投影。
 *
 * 断线时把「此刻在发生什么」那几档换成 `unknown`，其余原样留着。
 * 这就是设计稿那句「我把所有『进行中』都撤了」在代码里的样子——
 * 撤的是进行中，不是把整个界面清空。
 *
 * # presence 走同一个闸门，不另造一套
 *
 * `link === 'down'` 说的是「整条事件流断了」；`presence === 'unknown'`
 * 说的是「这一位多久没吭声了」。**两者在界面上要说的是同一句话**：
 * 此刻发生什么我们确认不了。所以它们折叠同一批档，终态同样留着——
 * 一件已经做完的活，不会因为没人吭声就变成没做完。
 *
 * 这是 PRD 验收 #9「任一未知状态不得显示 running」的前端那一半。
 * **注意它只改「显示成什么」，不改 durable 状态**：交接 §1.3 说得很清楚，
 * 写 state 要落 ledger、要动 fence，判错会打断一个正在干活的 Agent；
 * 而这里判错只是多显示一次「不知道」。两件事分开的意义就在这里——
 * 显示可以谨慎，durable 不可以草率。
 */
export function projectItemState(
  reported: ItemState | null,
  link: LinkState,
  presence: 'live' | 'unknown' = 'live',
): ItemState {
  if (reported === null) return 'unknown'
  const cannotConfirm = link === 'down' || presence === 'unknown'
  if (cannotConfirm && !SURVIVES_DISCONNECT.has(reported)) return 'unknown'
  return reported
}

/**
 * 要不要画那条 3px 进度条。
 *
 * 设计稿两处明写：`pct` 传数字才渲染，不传就没有条；`progress` 无事件时为 null，
 * **前端不得补 0 或补动画**。所以这里只有一个出口——拿不到数字就是不画，
 * 而不是画一条 0%。一条 0% 的进度条和一条真的停在 0% 的进度条，
 * 在屏幕上是同一个东西，而它们说的是完全不同的两件事。
 *
 * 断线时一律不画：那个百分比是断线之前的，画出来会被读成此刻的。
 */
export function progressBarPercent(
  progress: number | null | undefined,
  state: ItemState,
  link: LinkState,
  presence: 'live' | 'unknown' = 'live',
): number | null {
  if (link === 'down') return null
  // presence 未知时同样不画。设计包四处出处里最直白的一句是
  // LuluV3.dc.html:2599：「不显示进度条 —— 我没有它的进度，画一个就是编的。」
  //
  // 今天 `progress` 恒为 null（后端不给），所以这一行现在**碰不到**。
  // 留着不是防御性编程：哪天接上真进度，没有这一行它就会在失联的行上
  // 画一条断线前的百分比，而那条会被读成此刻的。
  if (presence === 'unknown') return null
  if (state === 'unknown') return null
  if (typeof progress !== 'number' || !Number.isFinite(progress)) return null
  return Math.max(0, Math.min(100, progress))
}
